import { mm, type Um } from './units.js';
import type { HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, DrawerSet, Front, FrontRect, FrontSpaceRef } from './document.js';
import { makePanel, partBox } from './geometry.js';
import { panelSize } from './assembly.js';
import { buildShelfSet } from './shelves.js';
import { drawerSlotRect } from './drawers.js';

/** Default gap between neighbouring fronts (fuga) — a setting of the assembly's front group. */
export const DEFAULT_FRONT_REVEAL = mm(3);
/** Default clearance of a front to the carcass edge / opening (zazor). */
export const DEFAULT_FRONT_CLEARANCE = mm(2);
/** Openings narrower than this (e.g. the side clearance of shelves) are not offered. */
const MIN_SPACE = mm(10);

/**
 * Fronts of free-panel assemblies. The front view (X/Y) of the panels that reach the front is split
 * into rectangular spaces (openings); a front covers one or several picked spaces. Spaces remember
 * the panels around them, so a front follows the carcass when it changes. A union of spaces that is
 * not a rectangle becomes a CNC front (its outline is listed separately in the cut list).
 */

const TOL = 500; // 0.5 mm
/** Panels whose front face is within this distance of the carcass front take part in the front view. */
const FRONT_REACH = mm(20);

interface Box {
  min: Vec3;
  max: Vec3;
}
const box = (p: AssemblyPanel): Box => {
  const s = panelSize(p);
  return { min: [...p.position], max: [p.position[0] + s[0], p.position[1] + s[1], p.position[2] + s[2]] };
};

export interface FrontSpace {
  ref: FrontSpaceRef;
  rect: FrontRect;
}

interface FrontView {
  zFront: Um;
  /** Panels that reach the front (keys and boxes). */
  solids: { key: string; b: Box }[];
  bbox: FrontRect;
}

/** Boxes of the generated shelves (key = shelf part key, e.g. `police-1-2`). */
function shelfBoxes(inst: AssemblyInstance): { key: string; b: Box }[] {
  const out: { key: string; b: Box }[] = [];
  for (const set of inst.shelfSets ?? [])
    for (const p of buildShelfSet(inst, set).parts) {
      const pb = partBox(p);
      out.push({ key: p.localKey, b: { min: pb.min, max: [pb.min[0] + pb.size[0], pb.min[1] + pb.size[1], pb.min[2] + pb.size[2]] } });
    }
  return out;
}

/** Box of a panel or a generated shelf by key. */
function solidBox(inst: AssemblyInstance, key: string | null): Box | null {
  if (!key) return null;
  const p = inst.panels.find((x) => x.key === key);
  if (p) return box(p);
  return shelfBoxes(inst).find((s) => s.key === key)?.b ?? null;
}

/** `betweenShelves`: generated shelves split the openings too. The outer box is taken from the panels only. */
function frontView(inst: AssemblyInstance, betweenShelves = false): FrontView | null {
  const all = inst.panels.filter((p) => !p.repeat || p.repeat.count <= 1).map((p) => ({ key: p.key, b: box(p) }));
  if (!all.length) return null;
  // Walls that run past a front (swapped overlap) stick out by the front thickness: not the front plane.
  const over = new Set((inst.fronts ?? []).flatMap((f) => f.wallsOver ?? []));
  const plane = all.filter((s) => !over.has(s.key));
  const zFront = Math.max(...(plane.length ? plane : all).map((s) => s.b.max[2]));
  const panels = all.filter((s) => s.b.max[2] >= zFront - FRONT_REACH);
  const solids = betweenShelves ? [...panels, ...shelfBoxes(inst).filter((s) => s.b.max[2] >= zFront - FRONT_REACH)] : panels;
  return {
    zFront,
    solids,
    bbox: { x0: Math.min(...panels.map((s) => s.b.min[0])), y0: Math.min(...panels.map((s) => s.b.min[1])), x1: Math.max(...panels.map((s) => s.b.max[0])), y1: Math.max(...panels.map((s) => s.b.max[1])) },
  };
}

const overlap = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);

/**
 * Openings of the front view, as rectangles, with the panels that bound them. With `betweenShelves`
 * the generated shelves split them as well (one opening per shelf gap).
 */
export function frontSpaces(inst: AssemblyInstance, opts: { betweenShelves?: boolean } = {}): FrontSpace[] {
  const v = frontView(inst, !!opts.betweenShelves);
  if (!v) return [];
  const uniq = (xs: number[]) => [...new Set(xs.map((x) => Math.round(x)))].sort((a, b) => a - b);
  const xs = uniq([v.bbox.x0, v.bbox.x1, ...v.solids.flatMap((s) => [s.b.min[0], s.b.max[0]])]).filter((x) => x >= v.bbox.x0 && x <= v.bbox.x1);
  const ys = uniq([v.bbox.y0, v.bbox.y1, ...v.solids.flatMap((s) => [s.b.min[1], s.b.max[1]])]).filter((y) => y >= v.bbox.y0 && y <= v.bbox.y1);
  const solidAt = (x: number, y: number) => v.solids.some((s) => x > s.b.min[0] && x < s.b.max[0] && y > s.b.min[1] && y < s.b.max[1]);

  // Free cells merged along rows into strips, then strips with the same width stacked into rectangles.
  const rects: FrontRect[] = [];
  let open = new Map<string, FrontRect>();
  for (let j = 0; j < ys.length - 1; j++) {
    const y0 = ys[j]!;
    const y1 = ys[j + 1]!;
    if (y1 - y0 <= TOL) continue;
    const strips: [number, number][] = [];
    let start: number | null = null;
    for (let i = 0; i < xs.length - 1; i++) {
      const free = xs[i + 1]! - xs[i]! > TOL && !solidAt((xs[i]! + xs[i + 1]!) / 2, (y0 + y1) / 2);
      if (free && start === null) start = xs[i]!;
      if (!free && start !== null) {
        strips.push([start, xs[i]!]);
        start = null;
      }
    }
    if (start !== null) strips.push([start, xs[xs.length - 1]!]);
    const next = new Map<string, FrontRect>();
    for (const [x0, x1] of strips) {
      const k = `${x0}:${x1}`;
      const prev = open.get(k);
      if (prev && prev.y1 === y0) {
        prev.y1 = y1;
        next.set(k, prev);
        open.delete(k);
      } else next.set(k, { x0, x1, y0, y1 });
    }
    for (const r of open.values()) rects.push(r);
    open = next;
  }
  for (const r of open.values()) rects.push(r);
  const kept = rects.filter((r) => r.x1 - r.x0 >= MIN_SPACE && r.y1 - r.y0 >= MIN_SPACE);
  rects.length = 0;
  rects.push(...kept);
  rects.sort((a, b) => b.y0 - a.y0 || a.x0 - b.x0);
  return withDrawerSlots(inst, describe(v, rects));
}

/** Openings that hold drawers become one opening per drawer (a drawer has its own front). */
function withDrawerSlots(inst: AssemblyInstance, spaces: FrontSpace[]): FrontSpace[] {
  const out: FrontSpace[] = [];
  for (const sp of spaces) {
    const set = (inst.drawerSets ?? []).find((d) => d.leftKey === sp.ref.left && d.rightKey === sp.ref.right && d.bottomKey === sp.ref.bottom && d.topKey === sp.ref.top);
    if (!set) {
      out.push(sp);
      continue;
    }
    for (let k = 1; k <= set.count; k++) {
      const rect = drawerSlotRect(inst, set, k)!;
      out.push({ rect, ref: { left: set.leftKey, right: set.rightKey, bottom: k === set.count ? set.bottomKey : null, top: k === 1 ? set.topKey : null, rect: { ...rect }, drawer: { setId: set.id, index: k } } });
    }
  }
  return out;
}

function describe(v: FrontView, rects: FrontRect[]): FrontSpace[] {
  const pick = (pred: (b: Box) => boolean, span: (b: Box) => number) => {
    let best: { key: string; o: number } | null = null;
    for (const s of v.solids) {
      if (!pred(s.b)) continue;
      const o = span(s.b);
      if (o > TOL && (!best || o > best.o)) best = { key: s.key, o };
    }
    return best?.key ?? null;
  };
  return rects.map((r) => ({
    rect: r,
    ref: {
      left: pick((b) => Math.abs(b.max[0] - r.x0) <= TOL, (b) => overlap(b.min[1], b.max[1], r.y0, r.y1)),
      right: pick((b) => Math.abs(b.min[0] - r.x1) <= TOL, (b) => overlap(b.min[1], b.max[1], r.y0, r.y1)),
      bottom: pick((b) => Math.abs(b.max[1] - r.y0) <= TOL, (b) => overlap(b.min[0], b.max[0], r.x0, r.x1)),
      top: pick((b) => Math.abs(b.min[1] - r.y1) <= TOL, (b) => overlap(b.min[0], b.max[0], r.x0, r.x1)),
      rect: { ...r },
    },
  }));
}

/** Current rectangle of a stored space (follows its bounding panels). */
function spaceRect(inst: AssemblyInstance, s: FrontSpaceRef): FrontRect {
  if (s.drawer) {
    const set = inst.drawerSets?.find((d) => d.id === s.drawer!.setId);
    const r = set && s.drawer.index <= set.count ? drawerSlotRect(inst, set, s.drawer.index) : null;
    return r ?? s.rect;
  }
  const b = (k: string | null) => solidBox(inst, k);
  const l = b(s.left);
  const r = b(s.right);
  const bo = b(s.bottom);
  const t = b(s.top);
  return { x0: l ? l.max[0] : s.rect.x0, x1: r ? r.min[0] : s.rect.x1, y0: bo ? bo.max[1] : s.rect.y0, y1: t ? t.min[1] : s.rect.y1 };
}

export interface FrontShape {
  /** Pieces of the front in the X/Y plane (assembly-local µm). */
  pieces: FrontRect[];
  bbox: FrontRect;
  /** Not a rectangle → CNC. */
  irregular: boolean;
  z0: Um;
  z1: Um;
}

/**
 * Outline of a front over the given spaces. Overlay: outer panel edges are covered up to the
 * clearance (zazor); an inner panel (divider, fixed shelf) is covered completely while there is no
 * front on its other side, and to its middle minus half the gap (fuga) once there is one. Joined
 * openings run over the panel between them. Inset: inside the opening with the clearance.
 */
export function frontShape(inst: AssemblyInstance, f: Pick<Front, 'spaces' | 'mode' | 'thickness'> & { id?: string; clearance?: Um; slice?: Front['slice']; wallsOver?: string[] }): FrontShape | null {
  const v = frontView(inst);
  if (!v || !f.spaces.length) return null;
  const rects = f.spaces.map((s) => spaceRect(inst, s));
  const thick = (k: string | null, axis: 0 | 1) => {
    const b = solidBox(inst, k);
    return b ? { t: b.max[axis] - b.min[axis], b } : null;
  };
  const gap = inst.frontReveal ?? DEFAULT_FRONT_REVEAL;
  const c = f.clearance ?? DEFAULT_FRONT_CLEARANCE;
  const others = (inst.fronts ?? []).filter((o) => o.id !== f.id);
  const pieces = f.spaces.map((s, i) => {
    const rect = rects[i]!;
    const side = (key: string | null, opposite: 'left' | 'right' | 'bottom' | 'top', axis: 0 | 1, isMax: boolean) => {
      // Another picked space on the far side of the same panel: the front runs over the panel.
      const joined = !!key && f.spaces.some((o, j) => j !== i && o[opposite] === key && (axis === 0 ? overlap(rects[j]!.y0, rects[j]!.y1, rect.y0, rect.y1) : overlap(rects[j]!.x0, rects[j]!.x1, rect.x0, rect.x1)) > TOL);
      const p = thick(key, axis);
      if (joined && p) return isMax ? p.t : 0;
      // This wall covers the front's edge: the front stops at the wall with the clearance.
      if (key && f.wallsOver?.includes(key)) return -c;
      if (!p && f.mode === 'overlay' && s.drawer && (opposite === 'top' || opposite === 'bottom')) {
        // Between two drawer slots: a divider board (or nothing). Like any inner panel — covered fully
        // without a front on the other side, shared with the fuga between when there is one.
        const next = s.drawer.index + (opposite === 'top' ? 1 : -1);
        const neighbour = others.some((o) => o.spaces.some((sp) => sp.drawer?.setId === s.drawer!.setId && sp.drawer.index === next));
        const d = inst.drawerSets?.find((x) => x.id === s.drawer!.setId)?.dividerThickness ?? 0;
        return neighbour ? (d - gap) / 2 : d - c;
      }
      if (f.mode === 'inset' || !p) return -c;
      const outer = isMax ? p.b.max[axis] >= (axis === 0 ? v.bbox.x1 : v.bbox.y1) - TOL : p.b.min[axis] <= (axis === 0 ? v.bbox.x0 : v.bbox.y0) + TOL;
      if (outer) return p.t - c;
      // Inner panel: shared with a front on the other side → half each with the gap between them.
      const span = (sp: FrontSpaceRef) => {
        const o = spaceRect(inst, sp);
        return axis === 0 ? overlap(o.y0, o.y1, rect.y0, rect.y1) : overlap(o.x0, o.x1, rect.x0, rect.x1);
      };
      const neighbour = others.some((o) => o.mode === 'overlay' && o.spaces.some((sp) => sp[opposite] === key && span(sp) > TOL));
      return neighbour ? (p.t - gap) / 2 : p.t - c;
    };
    return {
      x0: Math.round(rect.x0 - side(s.left, 'right', 0, false)),
      x1: Math.round(rect.x1 + side(s.right, 'left', 0, true)),
      y0: Math.round(rect.y0 - side(s.bottom, 'top', 1, false)),
      y1: Math.round(rect.y1 + side(s.top, 'bottom', 1, true)),
    };
  });
  const full = bboxOf(pieces);
  const cut = f.slice ? sliceRect(full, f.slice, gap) : full;
  const parts = f.slice
    ? pieces
        .map((p) => ({ x0: Math.max(p.x0, cut.x0), y0: Math.max(p.y0, cut.y0), x1: Math.min(p.x1, cut.x1), y1: Math.min(p.y1, cut.y1) }))
        .filter((p) => p.x1 - p.x0 > TOL && p.y1 - p.y0 > TOL)
    : pieces;
  if (!parts.length) return null;
  const bbox = bboxOf(parts);
  const irregular = unionArea(parts) < (bbox.x1 - bbox.x0) * (bbox.y1 - bbox.y0) - TOL * TOL;
  const z0 = f.mode === 'inset' ? v.zFront - f.thickness : v.zFront;
  return { pieces: parts, bbox, irregular, z0, z1: z0 + f.thickness };
}

const bboxOf = (rs: FrontRect[]): FrontRect => ({ x0: Math.min(...rs.map((p) => p.x0)), y0: Math.min(...rs.map((p) => p.y0)), x1: Math.max(...rs.map((p) => p.x1)), y1: Math.max(...rs.map((p) => p.y1)) });

/**
 * Part of a split front: fractions [start, end] of the whole front per axis; every inner cut leaves
 * the gap (fuga), so equal fractions give equal fronts.
 */
function sliceRect(r: FrontRect, slice: NonNullable<Front['slice']>, gap: Um): FrontRect {
  const cut = (a0: number, a1: number, s?: [number, number]): [number, number] => {
    if (!s) return [a0, a1];
    const span = a1 - a0 + gap;
    return [Math.round(a0 + s[0] * span), Math.round(a0 + s[1] * span - gap)];
  };
  const [x0, x1] = cut(r.x0, r.x1, slice.x);
  const [y0, y1] = cut(r.y0, r.y1, slice.y);
  return { x0, x1, y0, y1 };
}

/**
 * Splits a front in two: left/right (vertical cut) or bottom/top (horizontal cut). The original
 * front becomes the left/bottom part (keeps its id), a new front is the other part. Both keep the
 * openings, so they still follow the carcass. Hinges: left part left, right part right.
 */
export function splitFront(inst: AssemblyInstance, frontId: string, direction: 'vertical' | 'horizontal'): Front | null {
  const f = inst.fronts?.find((x) => x.id === frontId);
  if (!f) return null;
  const axis = direction === 'vertical' ? 'x' : 'y';
  const [s, e] = f.slice?.[axis] ?? [0, 1];
  const mid = (s + e) / 2;
  const other: Front = { ...structuredClone(f), id: nextFrontId(inst), slice: { ...f.slice, [axis]: [mid, e] } };
  f.slice = { ...f.slice, [axis]: [s, mid] };
  if (direction === 'vertical') {
    f.label = `${f.label} L`;
    other.label = `${other.label} D`;
    f.hinge = 'left';
    other.hinge = 'right';
  } else {
    f.label = `${f.label} dolje`;
    other.label = `${other.label} gore`;
  }
  inst.fronts!.splice(inst.fronts!.indexOf(f) + 1, 0, other);
  return other;
}

/** Area covered by axis-aligned rectangles (overlaps counted once). */
function unionArea(rs: FrontRect[]): number {
  const xs = [...new Set(rs.flatMap((r) => [r.x0, r.x1]))].sort((a, b) => a - b);
  const ys = [...new Set(rs.flatMap((r) => [r.y0, r.y1]))].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i < xs.length - 1; i++)
    for (let j = 0; j < ys.length - 1; j++) {
      const cx = (xs[i]! + xs[i + 1]!) / 2;
      const cy = (ys[j]! + ys[j + 1]!) / 2;
      if (rs.some((r) => cx > r.x0 && cx < r.x1 && cy > r.y0 && cy < r.y1)) area += (xs[i + 1]! - xs[i]!) * (ys[j + 1]! - ys[j]!);
    }
  return area;
}

export function nextFrontId(inst: AssemblyInstance): string {
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id), ...(inst.fronts ?? []).map((f) => f.id)]);
  let n = (inst.fronts ?? []).length + 1;
  while (used.has(`fronta-${n}`)) n++;
  return `fronta-${n}`;
}

export function makeFront(inst: AssemblyInstance, spaces: FrontSpace[]): Front {
  const n = (inst.fronts ?? []).length + 1;
  return {
    id: nextFrontId(inst),
    label: n === 1 ? 'Fronta' : `Fronta ${n}`,
    spaces: spaces.map((s) => structuredClone(s.ref)),
    mode: 'overlay',
    clearance: DEFAULT_FRONT_CLEARANCE,
    thickness: mm(18),
    // A drawer front has no hinges.
    hinge: spaces.some((s) => s.ref.drawer) ? 'none' : null,
    materialSlot: 'front',
    grain: 'length',
  };
}

/** Generic hinge count by the length of the hinged edge. */
export function hingeCount(edge: Um): number {
  return edge <= mm(900) ? 2 : edge <= mm(1600) ? 3 : edge <= mm(2000) ? 4 : 5;
}

export function buildFront(inst: AssemblyInstance, f: Front): { parts: PanelPart[]; hardware: HardwareItem[]; issues: Issue[] } {
  const issues: Issue[] = [];
  const say = (message: string, severity: 'warning' | 'error' | 'info' = 'warning') => issues.push({ severity, code: 'fronts', message: `${f.label}: ${message}`, cabinetId: inst.id, partId: `${inst.id}/${f.id}` });
  for (const s of f.spaces) for (const k of [s.left, s.right, s.bottom, s.top]) if (k && !inst.panels.some((p) => p.key === k)) say(`ploča „${k}” oko prostora više ne postoji — uzeta je zapamćena mjera.`);
  const shape = frontShape(inst, f);
  if (!shape) return { parts: [], hardware: [], issues };
  const { bbox } = shape;
  const w = bbox.x1 - bbox.x0;
  const h = bbox.y1 - bbox.y0;
  if (w <= 0 || h <= 0) {
    say('fronta nema površinu.', 'error');
    return { parts: [], hardware: [], issues };
  }
  if (shape.irregular) say('nepravilan oblik — fronta ide na CNC obradu, njene mjere su posebno u krojnoj listi.');
  if (f.hinge === null) say('izaberite stranu šarki.', 'info');
  const part = makePanel({
    cabinetId: inst.id,
    localKey: f.id,
    role: 'door',
    label: f.label,
    box: { min: [bbox.x0, bbox.y0, shape.z0], size: [w, h, f.thickness] },
    lengthAxis: 'y',
    widthAxis: 'x',
    materialSlot: f.materialSlot,
    grain: f.grain,
    ...(f.spaces.some((s) => s.drawer) ? { movable: 'slide' as const } : f.hinge === 'left' ? { movable: 'hinge-left' as const } : f.hinge === 'right' ? { movable: 'hinge-right' as const } : {}),
  });
  if (shape.irregular) part.cnc = { outline: shape.pieces.map((p) => ({ x0: p.x0 - bbox.x0, y0: p.y0 - bbox.y0, x1: p.x1 - bbox.x0, y1: p.y1 - bbox.y0 })) };
  if (f.hinge && f.hinge !== 'none') part.hingeSide = f.hinge;
  const hardware: HardwareItem[] = [];
  if (f.hinge && f.hinge !== 'none') {
    const edge = f.hinge === 'left' || f.hinge === 'right' ? h : w;
    hardware.push({ id: `${inst.id}/${f.id}-hinges`, cabinetId: inst.id, kind: 'hinge', label: 'Šarka (generička)', quantity: hingeCount(edge), generic: true, note: 'Broj šarki po dužini ivice (≤900 mm: 2, ≤1600: 3, ≤2000: 4); provjeriti prema modelu šarke i masi fronte.' });
  }
  return { parts: [part], hardware, issues };
}

export type FrontHinge = NonNullable<Front['hinge']>;
export const HINGE_LABEL: Record<FrontHinge, string> = { left: 'Lijevo', right: 'Desno', top: 'Gore', bottom: 'Dolje', none: 'Bez šarki' };

/** Which front a part local key is. */
export function frontOfPart(inst: AssemblyInstance, localKey: string): Front | null {
  return (inst.fronts ?? []).find((f) => f.id === localKey) ?? null;
}

/**
 * Why several picked openings cannot take one divider (null = fine): they must form one rectangle
 * with nothing but generated shelves inside — no panel in between, no unpicked opening.
 */
export function dividerSelectionError(inst: AssemblyInstance, selected: FrontSpace[], all: FrontSpace[]): string | null {
  if (!selected.length) return 'Izaberite prostor.';
  if (selected.some((sp) => sp.ref.drawer)) return 'U prostoru su ladice — pregrada ga ne može presjeći.';
  if (selected.length === 1) return null;
  const r = bboxOf(selected.map((sp) => spaceRect(inst, sp.ref)));
  const inside = (b: FrontRect) => overlap(b.x0, b.x1, r.x0, r.x1) > TOL && overlap(b.y0, b.y1, r.y0, r.y1) > TOL;
  const v = frontView(inst);
  if (v?.solids.some((s) => inside({ x0: s.b.min[0], x1: s.b.max[0], y0: s.b.min[1], y1: s.b.max[1] }))) return 'Između izabranih prostora je ploča — pregrada bi je presjekla.';
  const same = (a: FrontSpace, b: FrontSpace) => a.rect.x0 === b.rect.x0 && a.rect.x1 === b.rect.x1 && a.rect.y0 === b.rect.y0 && a.rect.y1 === b.rect.y1;
  if (all.some((sp) => !selected.some((x) => same(x, sp)) && inside(spaceRect(inst, sp.ref)))) return 'Izabrani prostori ne čine pravougaonik — izaberite susjedne prostore (i one između njih).';
  return null;
}

/**
 * "+ Pregrada": a plain panel that cuts one or several joined openings in the middle — vertical (a divider, thickness on
 * X) or horizontal (a fixed shelf, thickness on Y). Its depth is the depth shared by the panels
 * around the opening. After creation it is an ordinary panel.
 */
export function makeDivider(inst: AssemblyInstance, space: FrontSpace | FrontSpace[], direction: 'vertical' | 'horizontal', thickness: Um = mm(18)): AssemblyPanel {
  const spaces = Array.isArray(space) ? space : [space];
  const rect = bboxOf(spaces.map((sp) => spaceRect(inst, sp.ref)));
  // Depth from the panels around the openings (generated shelves may be set back).
  const around = spaces
    .flatMap((sp) => [sp.ref.left, sp.ref.right, sp.ref.bottom, sp.ref.top])
    .map((k) => inst.panels.find((p) => p.key === k))
    .filter((p): p is AssemblyPanel => !!p)
    .map(box);
  const all = inst.panels.map(box);
  const z0 = around.length ? Math.max(...around.map((b) => b.min[2])) : Math.min(...all.map((b) => b.min[2]));
  const z1 = around.length ? Math.min(...around.map((b) => b.max[2])) : Math.max(...all.map((b) => b.max[2]));
  return dividerPanels(inst, rect, z0, z1, direction, 1, thickness)[0]!;
}

/**
 * `count` dividers spread evenly across the picked openings: equal free gaps between the openings'
 * edges and the boards (one divider = in the middle). Plain panels with the next free keys.
 */
export function makeDividers(inst: AssemblyInstance, spaces: FrontSpace[], direction: 'vertical' | 'horizontal', count: number, thickness: Um = mm(18)): AssemblyPanel[] {
  if (count === 1) return [makeDivider(inst, spaces, direction, thickness)];
  const one = makeDivider(inst, spaces, direction, thickness);
  const rect = bboxOf(spaces.map((sp) => spaceRect(inst, sp.ref)));
  return dividerPanels(inst, rect, one.position[2], one.position[2] + one.width, direction, count, thickness);
}

function dividerPanels(inst: AssemblyInstance, rect: FrontRect, z0: Um, z1: Um, direction: 'vertical' | 'horizontal', count: number, thickness: Um): AssemblyPanel[] {
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id), ...(inst.fronts ?? []).map((f) => f.id), ...(inst.drawerSets ?? []).map((d) => d.id)]);
  let n = inst.panels.length + 1;
  const out: AssemblyPanel[] = [];
  const [a0, a1] = direction === 'vertical' ? [rect.x0, rect.x1] : [rect.y0, rect.y1];
  const gap = (a1 - a0 - count * thickness) / (count + 1);
  for (let i = 0; i < count; i++) {
    while (used.has(`ploca-${n}`)) n++;
    used.add(`ploca-${n}`);
    const at = Math.round(a0 + (i + 1) * gap + i * thickness);
    const base = { key: `ploca-${n}`, materialSlot: 'carcass' as const, grain: 'length' as const, repeat: null, joints: [], thickness, width: z1 - z0 };
    const label = count > 1 ? ` ${i + 1}` : '';
    out.push(
      direction === 'vertical'
        ? { ...base, label: `Pregrada${label}`, orientation: 'vertical-side', length: rect.y1 - rect.y0, position: [at, rect.y0, z0] }
        : { ...base, label: `Fiksna polica${label}`, orientation: 'horizontal', length: rect.x1 - rect.x0, position: [rect.x0, at, z0] },
    );
  }
  return out;
}


// ---------- picking openings and drawers behind fronts ----------

const touches = (a: FrontSpace, b: FrontSpace) => {
  const ov = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0) > TOL;
  const ya = ov(a.rect.y0, a.rect.y1, b.rect.y0, b.rect.y1);
  const xa = ov(a.rect.x0, a.rect.x1, b.rect.x0, b.rect.x1);
  return (
    (ya && ((!!a.ref.right && a.ref.right === b.ref.left) || (!!a.ref.left && a.ref.left === b.ref.right) || Math.abs(a.rect.x1 - b.rect.x0) <= TOL || Math.abs(a.rect.x0 - b.rect.x1) <= TOL)) ||
    (xa && ((!!a.ref.top && a.ref.top === b.ref.bottom) || (!!a.ref.bottom && a.ref.bottom === b.ref.top) || Math.abs(a.rect.y1 - b.rect.y0) <= TOL || Math.abs(a.rect.y0 - b.rect.y1) <= TOL))
  );
};

/**
 * Why `candidate` cannot be added to the openings already picked for one front (null = it can). A
 * drawer opening is never joined with anything (each drawer has its own front), and one front must be
 * one piece: the new opening has to touch one that is already picked.
 */
export function frontPickError(selected: FrontSpace[], candidate: FrontSpace): string | null {
  const same = (a: FrontSpace) => a.rect.x0 === candidate.rect.x0 && a.rect.y0 === candidate.rect.y0 && a.rect.x1 === candidate.rect.x1 && a.rect.y1 === candidate.rect.y1;
  if (!selected.length || selected.some(same)) return null;
  if (candidate.ref.drawer || selected.some((s) => s.ref.drawer)) return 'Ladica ima svoju frontu — ne spaja se s drugim prostorom.';
  if (!selected.some((s) => touches(s, candidate))) return 'Prostori se ne dodiruju — fronta mora biti iz jednog komada.';
  return null;
}

/** Drawers (set, number) whose slot a front covers. */
export function drawersBehindFront(inst: AssemblyInstance, f: Front): { set: DrawerSet; index: number }[] {
  const shape = frontShape(inst, f);
  if (!shape) return [];
  const out: { set: DrawerSet; index: number }[] = [];
  for (const set of inst.drawerSets ?? [])
    for (let k = 1; k <= set.count; k++) {
      const r = drawerSlotRect(inst, set, k);
      if (r && shape.pieces.some((p) => overlap(p.x0, p.x1, r.x0, r.x1) > TOL && overlap(p.y0, p.y1, r.y0, r.y1) > TOL)) out.push({ set, index: k });
    }
  return out;
}

/** Inset fronts reach into the carcass: drawers behind them get shorter so they close. Returns what changed. */
export function fitDrawersToFronts(inst: AssemblyInstance): { set: DrawerSet; index: number; setback: Um }[] {
  const changed: { set: DrawerSet; index: number; setback: Um }[] = [];
  for (const f of inst.fronts ?? []) {
    if (f.mode !== 'inset') continue;
    for (const { set, index } of drawersBehindFront(inst, f)) {
      const cur = set.setbacks?.[String(index)] ?? 0;
      if (cur >= f.thickness) continue;
      set.setbacks = { ...(set.setbacks ?? {}), [String(index)]: f.thickness };
      changed.push({ set, index, setback: f.thickness });
    }
  }
  return changed;
}

/** Puts the drawers behind a front back to their full depth. */
export function restoreDrawersBehindFront(inst: AssemblyInstance, f: Front): number {
  let n = 0;
  for (const { set, index } of drawersBehindFront(inst, f)) {
    if (!set.setbacks?.[String(index)]) continue;
    const { [String(index)]: _, ...rest } = set.setbacks;
    set.setbacks = rest;
    n++;
  }
  return n;
}


// ---------- swapping the overlap between an overlay front and one wall ----------

/** Which part covers the edge where an overlay front meets a panel around its openings (null = they do not meet). */
export function frontWallOverlap(inst: AssemblyInstance, frontId: string, panelKey: string): 'front' | 'wall' | null {
  const f = inst.fronts?.find((x) => x.id === frontId);
  const p = inst.panels.find((x) => x.key === panelKey);
  if (!f || !p || f.mode !== 'overlay' || p.orientation === 'vertical-front') return null;
  if (!f.spaces.some((s) => [s.left, s.right, s.bottom, s.top].includes(panelKey))) return null;
  return f.wallsOver?.includes(panelKey) ? 'wall' : 'front';
}

/**
 * Swaps who covers whom on one side of an overlay front: the wall runs forward by the front
 * thickness and covers the front's edge (the front stops at the wall), or back. The front stays an
 * overlay front everywhere else. Every overlay front at that wall changes with it, so they never
 * collide with the longer wall.
 */
export function swapFrontWall(inst: AssemblyInstance, frontId: string, panelKey: string): boolean {
  const state = frontWallOverlap(inst, frontId, panelKey);
  if (!state) return false;
  const f = inst.fronts!.find((x) => x.id === frontId)!;
  const p = inst.panels.find((x) => x.key === panelKey)!;
  const at = (inst.fronts ?? []).filter((o) => o.mode === 'overlay' && o.spaces.some((s) => [s.left, s.right, s.bottom, s.top].includes(panelKey)));
  // vertical-side and horizontal panels have their width along the depth (z)
  if (state === 'front') {
    p.width += f.thickness;
    for (const o of at) o.wallsOver = [...new Set([...(o.wallsOver ?? []), panelKey])];
  } else {
    p.width -= f.thickness;
    for (const o of at) {
      o.wallsOver = (o.wallsOver ?? []).filter((k) => k !== panelKey);
      if (!o.wallsOver.length) delete o.wallsOver;
    }
  }
  return true;
}
